/**
 * Geteilte TanStack-Query-Hooks fuer Betriebskalender-DayInfos.
 *
 * useDayInfos(year, month): EIN /calendar/day-infos-Call pro sichtbarem Monat
 * (Monatskalender AbsencesPage/TeamOverviewTab, BusinessDayRangePicker).
 * useDayInfosRange(from, to): DayInfos eines frei gewaehlten Zeitraums (Live-
 * Zaehler des Pickers). Query-Keys beginnen mit "calendar", damit die
 * Invalidierung nach Kalender-Aenderungen (z. B. ClosureRangeSheet) greift.
 */

import { keepPreviousData, useQuery } from "@tanstack/react-query"
import { api } from "@/services/api"
import type { DayInfo } from "@/types"

const pad = (n: number) => String(n).padStart(2, "0")

/** Gueltiges lokales Kalenderdatum "YYYY-MM-DD"? */
export function isIsoDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value)
}

/** Erster/letzter Tag eines Monats als "YYYY-MM-DD" */
export function monthBounds(year: number, month: number): { from: string; to: string } {
  const lastDay = new Date(year, month, 0).getDate()
  return {
    from: `${year}-${pad(month)}-01`,
    to: `${year}-${pad(month)}-${pad(lastDay)}`,
  }
}

/** Lookup-Map Datum -> DayInfo (fuer Zellen-Rendering) */
export function toDayMap(days: DayInfo[] | undefined): Map<string, DayInfo> {
  return new Map((days ?? []).map((d) => [d.date, d]))
}

const STALE_MS = 5 * 60 * 1000

/** DayInfos eines Monats — gecacht je (year, month) */
export function useDayInfos(year: number, month: number, enabled = true) {
  return useQuery({
    queryKey: ["calendar", "day-infos", year, month],
    queryFn: async () => {
      const { from, to } = monthBounds(year, month)
      return (await api.calendar.dayInfos(from, to)).days
    },
    staleTime: STALE_MS,
    enabled: enabled && year >= 1900 && year <= 2200 && month >= 1 && month <= 12,
    placeholderData: keepPreviousData,
  })
}

/** Maximale Zeitraum-Laenge fuer den Live-Zaehler (Schutz vor Tipp-Ausreissern) */
const MAX_RANGE_DAYS = 750

function rangeLengthDays(from: string, to: string): number {
  const [fy, fm, fd] = from.split("-").map(Number)
  const [ty, tm, td] = to.split("-").map(Number)
  return (Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86_400_000 + 1
}

/** DayInfos eines Zeitraums — nur aktiv bei gueltigem from <= to */
export function useDayInfosRange(from: string, to: string) {
  const valid =
    isIsoDate(from) &&
    isIsoDate(to) &&
    from <= to &&
    rangeLengthDays(from, to) <= MAX_RANGE_DAYS
  return useQuery({
    queryKey: ["calendar", "day-infos", "range", from, to],
    queryFn: async () => (await api.calendar.dayInfos(from, to)).days,
    staleTime: STALE_MS,
    enabled: valid,
    placeholderData: keepPreviousData,
  })
}
